import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	signal,
	Signal,
	ViewEncapsulation,
} from "@angular/core";
import {
	AvatarModule,
	ButtonModule,
	DividerModule,
	DropdownModule,
	IconModule,
	MenuItemModule,
	UiKitToasterService,
} from "@jack-sparrow-libraries/ui-kit";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { NavigationService } from "@services/navigation.service";
import { CommonModule } from "@angular/common";
import { SideNavActionsMenuComponent } from "@core/layout/components/side-nav/components/side-nav-actions-menu/side-nav-actions-menu.component";
import { SideNavItem } from "@core/layout/components/side-nav/models/side-nav-item.model";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { catchError, filter, map, shareReplay, switchMap, timeout } from "rxjs/operators";
import { DojoRoutesEnum, NavigationBase } from "@core/layout/enums/layout-routes.enum";
import { SideNavService } from "@core/layout/components/side-nav/services/side-nav.service";
import { firstValueFrom, Observable, of, startWith } from "rxjs";
import { FullClient } from "@models/full-client.model";
import { SiteVariable, SiteVariableApiService, SiteVariableNameEnum } from "features";
import { animate, style, transition, trigger } from "@angular/animations";
import { SideNavContactIds } from "@core/layout/components/side-nav/enums/side-nav-contact-ids.enum";
import { EmotionEventsHelperService, StrutsActionType } from "@services/helpers/emotion-events-helper.service";
import { SignalementBugService } from "@services/signalement-bug.service";
import { ClientDroits } from "@jack-sparrow-libraries/emotion-models";
import { LinkUtils } from "@core/layout/utils/link.utils";
import { LayoutService } from "@core/layout/services/layout.service";
import { SideNavIds } from "@core/layout/components/side-nav/enums/side-nav-ids.enum";
import { PaiementMenuItemComponent } from "@core/layout/components/side-nav/components/paiement-menu-item/paiement-menu-item.component";
import { AgatheYouCareService } from "@services/agathe-you-care.service";
import { AgatheYouBranding } from "@enums/agathe-you-branding.enum";
import { AgatheYouCareAssets } from "@core/constantes/agathe-you-care.constant";

type SignalBasicState<Data = unknown> = {
	loading: boolean;
	error: unknown | null;
	data: Data | null;
};

@Component({
	selector: "cba-side-nav",
	templateUrl: "./side-nav.component.html",
	styleUrls: ["./side-nav.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		CommonModule,
		MenuItemModule,
		AvatarModule,
		IconModule,
		DropdownModule,
		RouterModule,
		ButtonModule,
		SideNavActionsMenuComponent,
		DividerModule,
		PaiementMenuItemComponent,
	],
	host: { class: "h-full w-full" },
	animations: [
		trigger("sideNavAnimation", [
			transition(":enter", [
				style({
					opacity: 0,
					transform: "translateX(-20px)",
				}),
				animate(
					"200ms cubic-bezier(0.4, 0, 0.2, 1)",
					style({
						opacity: 1,
						transform: "translateX(0)",
					}),
				),
			]),
		]),
		trigger("itemEnterAnimation", [
			transition(":enter", [
				style({
					opacity: 0,
					transform: "translateX(-10px)",
				}),
				animate(
					"200ms cubic-bezier(0.4, 0, 0.2, 1)",
					style({
						opacity: 1,
						transform: "translateX(0)",
					}),
				),
			]),
		]),
	],
})
export class SideNavComponent {
	private readonly router = inject(Router);
	private readonly navigationService = inject(NavigationService);
	private readonly sideNavService = inject(SideNavService);
	private readonly siteVariableApiService = inject(SiteVariableApiService);
	private readonly toasterService = inject(UiKitToasterService);
	private readonly signalementBugService = inject(SignalementBugService);
	private readonly layoutService = inject(LayoutService);
	private readonly agatheYouCareService = inject(AgatheYouCareService);

	readonly $fullClient = input.required<FullClient>({ alias: "fullClient" });
	readonly $loading = input.required<boolean>({ alias: "loading" });

	readonly $droits = input.required<ClientDroits>({ alias: "droits" });

	readonly $canReportBug = computed(
		() =>
			this.$droits() >= ClientDroits.ConseillerPaiementGaranti || this.$fullClient()?.roles.includes("BETA_USER"),
	);

	private readonly fullClient$ = toObservable(this.$fullClient);

	private readonly horizonComptaRole$ = this.fullClient$.pipe(
		map((fullClient) => {
			if (fullClient?.roles.includes("COMPTABILITE_MAESTRO")) {
				return SiteVariableNameEnum.URL_COMPTA_MAESTRO;
			} else if (fullClient?.roles.includes("COMPTABILITE_TEMPO")) {
				return SiteVariableNameEnum.URL_COMPTA_TEMPO;
			} else {
				return null;
			}
		}),
	);

	private readonly hasHorizonComptaRole$ = this.horizonComptaRole$.pipe(
		map(Boolean),
		startWith(false),
		shareReplay(1),
	);

	readonly $hasHorizonComptaRole = toSignal(this.hasHorizonComptaRole$);

	private readonly horizonComptaUrl$ = this.horizonComptaRole$.pipe(
		filter(Boolean),
		switchMap((variableName) =>
			this.siteVariableApiService.getSiteVariableByName(variableName).pipe(
				map<SiteVariable, SignalBasicState<string>>((variable) => ({
					loading: false,
					error: null,
					data: variable.value,
				})),
				catchError<any, Observable<SignalBasicState<string>>>((error) =>
					of({ loading: false, error, data: null }),
				),
				startWith<SignalBasicState<string>>({ loading: true, error: null, data: null }),
			),
		),
		startWith<SignalBasicState<string>>({ loading: true, error: null, data: null }),
		shareReplay(1),
	);

	private readonly $horizonComptaUrl = toSignal(this.horizonComptaUrl$);

	// les paths ressemblent à :
	// /app/v1#admin-market/tdb
	// /app/v2/admin
	readonly $path = toSignal(
		this.router.events.pipe(
			filter((e) => e instanceof NavigationEnd),
			map((r: NavigationEnd) => r.urlAfterRedirects),
			startWith(location.hash.slice(1)),
		),
	);

	// Exposé au template pour distinguer l'item PAIEMENT et déléguer son rendu
	protected readonly SideNavIds = SideNavIds;
	protected readonly AgatheYouBranding = AgatheYouBranding;
	protected readonly AgatheYouCareAssets = AgatheYouCareAssets;

	protected readonly $branding = this.agatheYouCareService.$branding;

	readonly $menuItems = computed(() => {
		const url = this.$path();
		const fullClient = this.$fullClient();
		const items = this.determineSideNavRoutes(url, fullClient);
		return this.withActiveStates(items);
	});

	readonly $showAddMenuToogle: Signal<boolean> = computed(() => {
		const url = this.$path();
		const forbiddenShowAddOptionsURL = this.sideNavService.routesWhereAddOptionsAreHidden;
		return url && forbiddenShowAddOptionsURL.every((forbiddenURL) => !url.includes(forbiddenURL));
	});

	readonly $openAddMenu = signal(false);

	readonly intercomMenuItem: SideNavItem<SideNavContactIds> = {
		id: SideNavContactIds.CONTACT,
		icon: "info-circle",
		active: false,
		label: "Contact",
		link: SideNavContactIds.CONTACT,
		enfants: [
			{
				id: SideNavContactIds.SERVICE_CLIENT,
				icon: null,
				label: "Service client",
				link: SideNavContactIds.SERVICE_CLIENT,
			},
			{
				id: SideNavContactIds.SUGGERER_UNE_IDEE,
				icon: null,
				label: "Suggérer une idée",
				link: SideNavContactIds.SUGGERER_UNE_IDEE,
			},
		],
	};

	async onMenu(link: string) {
		if (!link) {
			return;
		}
		if (link.includes(":idPatient")) {
			const idPatient = await this.layoutService.selectPatient();
			if (!idPatient) {
				return;
			}
			link = LinkUtils.replaceUrlParams(link, {
				idPatient,
			});
		}
		this.navigationService.navigateByUrl(link);
	}

	onMenuContact(idStr: string): void {
		const id = idStr as SideNavContactIds;
		let action: StrutsActionType;
		if (id === SideNavContactIds.CONTACT || id === SideNavContactIds.SERVICE_CLIENT) {
			action = StrutsActionType.TOGGLE_INTERCOM;
		} else {
			action = StrutsActionType.OPEN_IDEAS_DIALOG;
		}
		if (action) {
			EmotionEventsHelperService.dispatchEvent({
				action,
				payload: null,
			});
		}
	}

	onReportBug() {
		this.signalementBugService.openReportBugSidepanel();
	}

	goToDashboard() {
		this.navigationService.navigateByBase(NavigationBase.DOJO, DojoRoutesEnum.DASHBOARD);
	}

	private isActive(link: string): boolean {
		const modelUrl = new URL(location.origin + link);
		const urlTree = this.router.createUrlTree([modelUrl.pathname], {
			fragment: modelUrl.hash.slice(1) || undefined,
			queryParams: modelUrl.searchParams,
		});
		console.log("isActive: " + link, {
			1: this.router.isActive(urlTree, {
				paths: "exact",
				queryParams: "subset",
				fragment: "exact",
				matrixParams: "ignored",
			}),
			2: this.router.isActive(urlTree, {
				paths: "exact",
				queryParams: "subset",
				fragment: "ignored",
				matrixParams: "ignored",
			}),
			3: this.compareFragments(modelUrl),
		});
		return (
			this.router.isActive(urlTree, {
				paths: "exact",
				queryParams: "subset",
				fragment: "exact",
				matrixParams: "ignored",
			}) ||
			(this.router.isActive(urlTree, {
				paths: "exact",
				queryParams: "subset",
				fragment: "ignored",
				matrixParams: "ignored",
			}) &&
				this.compareFragments(modelUrl))
		);
	}

	/**
	 * Compare le fragment de l'URL actuelle avec un model d'url (avec des params, ex : ":idPatient") pour déterminer si le fragment correspond à la route même si les params sont différents
	 * @param modelUrl
	 * @private
	 */
	private compareFragments(modelUrl: URL): boolean {
		const currentUrl = new URL(location.href);
		const currentHash = currentUrl.hash.slice(1);
		const newHash = modelUrl.hash.slice(1);

		if (!newHash) {
			return false;
		}

		if (modelUrl.hash.includes(":")) {
			// Remplace chaque :param par un pattern qui matche un segment dynamique
			const regexStr = "^" + newHash.replace(/:(\w+)/g, "[^/]+") + "$";
			const regex = new RegExp(regexStr);
			return regex.test(currentHash);
		}

		// Pas de paramètre dynamique : comparaison directe
		return currentHash === newHash;
	}

	private determineSideNavRoutes(url: string, fullClient: FullClient) {
		const sideNavEnum = this.sideNavService.getSideNavRouteEnumFromUrl(url);
		return this.sideNavService.generateItems(sideNavEnum, fullClient);
	}

	private withActiveStates(items: SideNavItem[]): SideNavItem[] {
		return items.map((item) => {
			const enfants = item.enfants?.map((subMenu) => ({
				...subMenu,
				active: subMenu.link ? this.isActive(subMenu.link) : false,
			}));
			const active = this.isActive(item.link) || (enfants?.some((subMenu) => subMenu.active) ?? false);
			return {
				...item,
				active,
				enfants,
			};
		});
	}

	async openHorizonCompta() {
		try {
			let state = this.$horizonComptaUrl();

			if (state?.loading) {
				state = await this.waitUntilHorizonComptaIsLoaded();
			}

			if (!state?.loading && !state.error && state.data) {
				window.open(state.data, "_blank");
			} else {
				throw new Error(
					state?.error
						? `Error fetching Horizon Compta URL: ${state.error}`
						: `Horizon Compta URL not found: ${state}`,
				);
			}
		} catch (error) {
			console.error(error);
			this.toasterService.show({
				color: "alert",
				titre: "Impossible de vous rediriger vers Horizon Compta.",
			});
		}
	}

	private async waitUntilHorizonComptaIsLoaded() {
		const loadingTimeout = 10000;

		const toaster = this.toasterService.show({
			color: "default",
			titre: "Chargement en cours...",
			extendedTimeout: loadingTimeout,
		});

		const state = await firstValueFrom(
			this.horizonComptaUrl$.pipe(
				filter((s) => !s.loading),
				timeout(loadingTimeout),
			),
		);

		if (toaster.toastRef) {
			toaster.toastRef.close();
		}

		return state;
	}
}
